import { cache } from 'react';
import { featureFlipping, Locale } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, ProductsRepositoryType } from '../../../../DI';
import { getCachedLoyalty, getCachedVipBrands, ProductDetailValue } from '../../../../server';

/**
 * Fetches product detail information with cache capabilities.
 *
 * @param {Locale} locale - The locale for the request.
 * @param {string} productId - The ID of the product to fetch.
 * @param {VipBrandsValue} vipBrands - VIP brands information.
 * @param {PaymentMethodsValue} paymentMethods - Payment methods information.
 * @returns {Promise<ProductDetailValue | undefined>} The detailed product information or undefined if not found.
 */
export const getProductDetailInfo = cache(
    async (locale: Locale, productId: string): Promise<ProductDetailValue | undefined> => {
        const [loyaltyConfigResult, vipBrandsResult] = await Promise.all([
            featureFlipping.isLoyaltyConfigEnabled ? getCachedLoyalty(locale) : Promise.resolve(undefined),
            getCachedVipBrands(locale),
        ]);

        const loyaltyConfig = unwrapResultSuccess(loyaltyConfigResult);

        const vipBrands = unwrapResultSuccess(vipBrandsResult) ?? {};

        const productResult = await CommonDI.get(ProductsRepositoryType).getProductById({
            locale,
            id: productId,
            vipBrands,
            needScope: true,
            isPreviewable: true,
            loyaltyConfig,
            variantIds: [],
        });

        if (productResult.type === 'empty' || productResult.type === 'failure') {
            return undefined;
        }

        return productResult.data;
    },
);
